import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { authFetch } from '../../utils/api';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from '../../utils/permissions';
import { InfraDomainTab } from './InfraDomainTab';

const { Title } = Typography;

type DomainStatus = 'active' | 'standby' | 'retired' | 'burned';
type DomainPurpose =
  | 'anytls'
  | 'shadowtls'
  | 'sk6ws'
  | 'vless_reality'
  | 'reserved';

interface DomainRecord {
  id: number;
  domain: string;
  purpose: DomainPurpose;
  status: DomainStatus;
  registrar: string | null;
  dnsProvider: string | null;
  certNotAfter: string | null;
  certLastCheckedAt: string | null;
  priority: number;
  weight: number;
  burnedReason: string | null;
}

const statusColor: Record<DomainStatus, string> = {
  active: 'green',
  standby: 'blue',
  retired: 'default',
  burned: 'red',
};

const statusOptions = [
  { label: 'active', value: 'active' },
  { label: 'standby', value: 'standby' },
  { label: 'retired', value: 'retired' },
  { label: 'burned', value: 'burned' },
];

const purposeOptions = [
  { label: 'anytls', value: 'anytls' },
  { label: 'shadowtls', value: 'shadowtls' },
  { label: 'sk6ws', value: 'sk6ws' },
  { label: 'vless_reality', value: 'vless_reality' },
  { label: 'reserved', value: 'reserved' },
];

async function apiFetch(url: string, options?: RequestInit) {
  const res = await authFetch(url, options);
  return res.json();
}

const DomainPoolTab: React.FC = () => {
  const sharedInfraReadOnly = !canMutateSharedInfra();
  const [data, setData] = useState<DomainRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DomainRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiFetch('/api/admin/domains');
      if (result.code === 0) {
        setData(Array.isArray(result.data) ? result.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAdd = () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      purpose: 'reserved',
      status: 'standby',
      priority: 100,
      weight: 100,
    });
    setModalOpen(true);
  };

  const openEdit = (record: DomainRecord) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const values = await form.validateFields();
    setSaving(true);
    try {
      const url = editing
        ? `/api/admin/domains/${editing.id}`
        : '/api/admin/domains';
      const method = editing ? 'PUT' : 'POST';
      const result = await apiFetch(url, {
        method,
        body: JSON.stringify(values),
      });
      if (result.code === 0) {
        message.success(editing ? '域名已更新' : '域名已新增');
        setModalOpen(false);
        fetchData();
      } else {
        message.error(result.msg || '操作失敗');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const result = await apiFetch(`/api/admin/domains/${id}`, {
      method: 'DELETE',
    });
    if (result.code === 0) {
      message.success('域名已刪除');
      fetchData();
    } else {
      message.error(result.msg || '刪除失敗');
    }
  };

  const handleProbe = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const result = await apiFetch(`/api/admin/domains/${id}/probe`, {
      method: 'POST',
    });
    if (result.code === 0) {
      message.success(result.data?.error ? 'Probe 失敗，已記錄' : 'Probe 成功');
      fetchData();
    } else {
      message.error(result.msg || 'Probe 失敗');
    }
  };

  const handleBurn = async (record: DomainRecord) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const result = await apiFetch(`/api/admin/domains/${record.id}/burn`, {
      method: 'POST',
      body: JSON.stringify({ reason: 'manual' }),
    });
    if (result.code === 0) {
      message.success('已標記 burned');
      fetchData();
    } else {
      message.error(result.msg || '操作失敗');
    }
  };

  return (
    <div className="page-enter">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          gap: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          域名池
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={sharedInfraReadOnly}
          onClick={openAdd}
        >
          新增域名
        </Button>
      </div>

      <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 12 }} />

      <Table
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{ pageSize: 50 }}
      >
        <Table.Column dataIndex="id" title="ID" width={70} />
        <Table.Column dataIndex="domain" title="域名" width={240} />
        <Table.Column dataIndex="purpose" title="用途" width={120} />
        <Table.Column
          dataIndex="status"
          title="狀態"
          width={100}
          render={(status: DomainStatus) => (
            <Tag color={statusColor[status]}>{status}</Tag>
          )}
        />
        <Table.Column dataIndex="priority" title="優先級" width={90} />
        <Table.Column dataIndex="weight" title="權重" width={80} />
        <Table.Column
          dataIndex="certNotAfter"
          title="TLS 到期"
          width={180}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
        <Table.Column
          dataIndex="burnedReason"
          title="封鎖原因"
          width={200}
          ellipsis
          render={(value: string | null) => value || '-'}
        />
        <Table.Column
          title="操作"
          width={260}
          fixed="right"
          render={(_: unknown, record: DomainRecord) => (
            <Space wrap>
              <Button
                size="small"
                disabled={sharedInfraReadOnly}
                onClick={() => openEdit(record)}
              >
                編輯
              </Button>
              <Button
                size="small"
                icon={<SyncOutlined />}
                disabled={sharedInfraReadOnly}
                onClick={() => handleProbe(record.id)}
              >
                Probe
              </Button>
              <Button
                size="small"
                danger
                disabled={sharedInfraReadOnly || record.status === 'burned'}
                onClick={() => handleBurn(record)}
              >
                Burn
              </Button>
              <Popconfirm
                title="確定刪除此域名？"
                onConfirm={() => handleDelete(record.id)}
              >
                <Button size="small" danger disabled={sharedInfraReadOnly}>
                  刪除
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={editing ? `編輯域名：${editing.domain}` : '新增域名'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="domain"
            label="域名"
            rules={[{ required: true, message: '請輸入域名' }]}
          >
            <Input placeholder="relay.example.com" />
          </Form.Item>
          <Form.Item name="purpose" label="用途">
            <Select options={purposeOptions} />
          </Form.Item>
          <Form.Item name="status" label="狀態">
            <Select options={statusOptions} />
          </Form.Item>
          <Form.Item name="registrar" label="Registrar">
            <Input placeholder="porkbun / namecheap / njalla" />
          </Form.Item>
          <Form.Item name="dnsProvider" label="DNS Provider">
            <Input placeholder="cloudflare / dnsimple / desec" />
          </Form.Item>
          <Form.Item name="priority" label="優先級">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="weight" label="權重">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="burnedReason" label="封鎖原因">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

/**
 * 域名管理頁 — 兩個 tab:
 *   - 域名池:馬甲域名輪替池 + cert 到期監控（既有）
 *   - 基礎設施域名:region → cert 子域映射（域名 config 化，後台一次改動）
 */
export const DomainPoolList: React.FC = () => {
  return (
    <div className="page-enter">
      <Tabs
        defaultActiveKey="pool"
        items={[
          { key: 'pool', label: '域名池', children: <DomainPoolTab /> },
          {
            key: 'infra',
            label: '基礎設施域名',
            children: <InfraDomainTab />,
          },
        ]}
      />
    </div>
  );
};
